import * as React from 'react';
import { useQueries, useQuery } from 'react-query';
import { SellersQuery } from '@seller-hermes/features/sellers/queries/seller.query';
import { FetchKeys } from '@seller-hermes/features/ui/constants';
import { Seller } from './sellers-selector.types';

export const SELLERS_LOOKUP_PAGE_SIZE = 1000;
export const SELLERS_LOOKUP_QUERY_KEY = [FetchKeys.ME, 'sellers-lookup'];

const LOOKUP_CONCURRENCY = 4;

const pageQueryKey = (from: number) => [...SELLERS_LOOKUP_QUERY_KEY, from];

// SUP-425/426: antes, cada página só era buscada depois que a anterior
// respondia, e uma falha tentava de novo 3x (default do react-query, com
// backoff exponencial) antes de desistir — uma única página azarada podia
// travar o carregamento inteiro por minutos (visto na prática: uma
// instabilidade pontual do backend deixava a aba travada, com o Firefox
// chegando a avisar "this page is slowing down"). Retry manual aqui, sem
// backoff, pra manter o pior caso previsível.
const fetchPageWithRetry = async (from: number, size: number) => {
  try {
    return await SellersQuery.lookup(from, size);
  } catch {
    return SellersQuery.lookup(from, size);
  }
};

/**
 * Carrega a lista rica de filiais do seletor/picker em segundo plano
 * (SUP-425/426): nenhuma resposta fica proporcional ao total de filiais do
 * grupo. A 1ª página carrega sozinha — o popup abre assim que ela chega —
 * e as páginas seguintes são buscadas em lotes concorrentes (não mais uma
 * de cada vez), até LOOKUP_CONCURRENCY por lote.
 *
 * Uma página que falhe mesmo depois do retry não trava as outras: fica de
 * fora do resultado e liga `hasLoadError`, pro popup avisar o usuário em
 * vez de ficar preso indefinidamente esperando ela.
 *
 * NÃO use `sellers`/`total` daqui como fonte de "o usuário tem acesso a
 * esta filial" — enquanto o carregamento em segundo plano roda (ou se
 * `hasLoadError` for true), a lista está incompleta por definição. Pra
 * isso existe `SellersQuery.lookupIds()` (sempre completo).
 */
export const useSellersLookup = () => {
  const firstPage = useQuery(
    pageQueryKey(0),
    () => fetchPageWithRetry(0, SELLERS_LOOKUP_PAGE_SIZE),
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: false },
  );

  /**
   * QUEM SABE O TOTAL PRIMEIRO MANDA.
   *
   * As páginas seguintes não precisam do CONTEÚDO da 1ª — precisam só do
   * total, pra saber quantas existem. Amarrar as duas coisas serializava o
   * carregamento: a 2ª página só arrancava quando a 1ª respondia (medido no
   * São João: 1.234→3.893 ms e só então 3.943→12.332 ms).
   *
   * `lookupIds()` responde a MESMA pergunta e já é buscado pelo seletor
   * (mesma chave → mesma entrada de cache, sem request novo). No backend os
   * dois usam o MESMO `WHERE` — `seller.id IN (ids do usuário)` +
   * `seller_group_id` do grupo ativo — então a contagem é idêntica por
   * construção, não por coincidência (medido no São João: 1.271 = 1.271).
   *
   * `??` pega o que chegar primeiro: em grupo pequeno a 1ª página ganha e
   * nada muda; em grupo grande o lookupIds ganha e as páginas saem juntas.
   */
  const idsQuery = useQuery(
    [FetchKeys.ME, 'sellers-lookup-ids'],
    () => SellersQuery.lookupIds(),
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000, retry: 1 },
  );

  const total = firstPage.data?.total ?? idsQuery.data?.length ?? 0;

  const remainingOffsets = React.useMemo(() => {
    const offsets: number[] = [];
    for (
      let from = SELLERS_LOOKUP_PAGE_SIZE;
      from < total;
      from += SELLERS_LOOKUP_PAGE_SIZE
    ) {
      offsets.push(from);
    }
    return offsets;
  }, [total]);

  // Janela de concorrência: só os índices < windowSize ficam habilitados.
  // Quando todas as páginas da janela atual terminam (sucesso ou erro), a
  // janela cresce em blocos de LOOKUP_CONCURRENCY.
  const [windowSize, setWindowSize] = React.useState(LOOKUP_CONCURRENCY);

  const restPages = useQueries(
    remainingOffsets.map((from, index) => ({
      queryKey: pageQueryKey(from),
      queryFn: () => fetchPageWithRetry(from, SELLERS_LOOKUP_PAGE_SIZE),
      // 🚨 SEM `Boolean(firstPage.data)`. `remainingOffsets` já deriva de
      // `total`, que só é > 0 quando ALGUMA das duas fontes respondeu — então
      // esta lista está vazia até haver total, e o `index < windowSize`
      // continua segurando a concorrência. Exigir a 1ª página aqui era o que
      // serializava sem necessidade.
      enabled: index < windowSize,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  );

  React.useEffect(() => {
    if (windowSize >= remainingOffsets.length) return;
    const currentWindow = restPages.slice(0, windowSize);
    const windowSettled =
      currentWindow.length > 0 &&
      currentWindow.every((q) => q.isSuccess || q.isError);
    if (windowSettled) {
      setWindowSize((w) =>
        Math.min(w + LOOKUP_CONCURRENCY, remainingOffsets.length),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restPages, windowSize, remainingOffsets.length]);

  const sellers = React.useMemo<Seller[]>(() => {
    const firstData = (firstPage.data?.data ?? []) as unknown as Seller[];
    const restData = restPages.flatMap((q) =>
      q.isSuccess
        ? ((q.data as { data?: unknown[] } | undefined)?.data ?? [])
        : [],
    ) as Seller[];
    return [...firstData, ...restData];
  }, [firstPage.data, restPages]);

  const hasLoadError = firstPage.isError || restPages.some((q) => q.isError);

  // True enquanto qualquer página (ativa no lote atual ou ainda esperando
  // sua vez) não chegou a um estado terminal — não cai pra false entre
  // lotes, só quando tudo (sucesso ou erro) já assentou.
  const isLoadingMore =
    firstPage.isLoading || restPages.some((q) => !q.isSuccess && !q.isError);

  return {
    sellers,
    total,
    // Só true até a 1ª página chegar — o popup abre sem esperar o resto.
    isLoading: firstPage.isLoading,
    isLoadingMore,
    // Alguma página não carregou mesmo depois do retry — a lista pode
    // estar incompleta; o consumidor deve avisar o usuário.
    hasLoadError,
  };
};
